import { DeletionFailureReason, UploadFailureReason } from '../../../../../../../../backend/src/types/types';
import { FileHistoryStats } from '@/api/queries/useGetFileHistoryStats';

export interface FailureReasonConfig {
  label: string;
  icon: string;
}

// Need to use hex colours rather than tailwind classes as the chart library doesn't support tailwind classes
export interface DeletionFailureConfig {
  label: string;
  description: string;
  resolution: string;
}

/** User-facing copy for each deletion failure reason. Unknown reasons fall back to the Unknown entry. */
export const DELETION_FAILURE_CONFIG: Record<DeletionFailureReason, DeletionFailureConfig> = {
  [DeletionFailureReason.DatabaseError]: {
    label: 'Database error',
    description: 'A database error occurred during deletion. Some documents may have been deleted before the failure.',
    resolution: 'Try again. If the issue persists, contact support.',
  },
  [DeletionFailureReason.StorageError]: {
    label: 'Storage error',
    description:
      'An error occurred while removing files from storage. Document records were deleted, but file cleanup may be incomplete.',
    resolution: 'Try again. If the issue persists, contact support.',
  },
  [DeletionFailureReason.Stalled]: {
    label: 'Job stalled',
    description: 'The deletion job did not complete within the expected time and was marked as failed.',
    resolution: 'Try again. If the issue persists, contact support.',
  },
  [DeletionFailureReason.Unknown]: {
    label: 'Unknown error',
    description: 'An unexpected error occurred during deletion.',
    resolution: 'Try again. If the issue persists, contact support.',
  },
};

export const FAILURE_REASON_CONFIG: Record<UploadFailureReason, FailureReasonConfig> = {
  [UploadFailureReason.Duplicate]: {
    label: 'Duplicate',
    icon: 'lucide--copy',
  },
  [UploadFailureReason.UnsupportedFileFormat]: {
    label: 'Unsupported Format',
    icon: 'lucide--file-x',
  },
  [UploadFailureReason.NoFileAvailable]: {
    label: 'File Not Found',
    icon: 'lucide--file-question',
  },
  [UploadFailureReason.DownloadFailed]: {
    label: 'Could Not Download',
    icon: 'lucide--cloud-off',
  },
  [UploadFailureReason.StorageError]: {
    label: 'Storage Issue',
    icon: 'lucide--hard-drive',
  },
  [UploadFailureReason.EncryptedFile]: {
    label: 'Password-protected file',
    icon: 'lucide--lock-keyhole',
  },
  [UploadFailureReason.InvalidArchive]: {
    label: 'Invalid Archive',
    icon: 'lucide--file-archive',
  },
  [UploadFailureReason.UnsafeFilePath]: {
    label: 'Unsafe File Path',
    icon: 'lucide--shield-alert',
  },
  [UploadFailureReason.FileTooLarge]: {
    label: 'File Too Large',
    icon: 'lucide--file-warning',
  },
  [UploadFailureReason.TooManyEntries]: {
    label: 'Too Many Files',
    icon: 'lucide--files',
  },
  [UploadFailureReason.UploadError]: {
    label: 'Upload Failed',
    icon: 'lucide--upload',
  },
  [UploadFailureReason.PdfConversionFailed]: {
    label: 'Could Not Convert',
    icon: 'lucide--file-x',
  },
  [UploadFailureReason.EmptyFile]: {
    label: 'Empty File',
    icon: 'lucide--file-minus',
  },
};

export const DEFAULT_STATS: FileHistoryStats = {
  totalAttempted: 0,
  totalSuccessful: 0,
  totalFailed: 0,
  uploadCount: 0,
  failuresByReason: {
    [UploadFailureReason.Duplicate]: 0,
    [UploadFailureReason.UnsupportedFileFormat]: 0,
    [UploadFailureReason.NoFileAvailable]: 0,
    [UploadFailureReason.DownloadFailed]: 0,
    [UploadFailureReason.StorageError]: 0,
    [UploadFailureReason.EncryptedFile]: 0,
    [UploadFailureReason.InvalidArchive]: 0,
    [UploadFailureReason.UnsafeFilePath]: 0,
    [UploadFailureReason.FileTooLarge]: 0,
    [UploadFailureReason.TooManyEntries]: 0,
    [UploadFailureReason.UploadError]: 0,
    [UploadFailureReason.PdfConversionFailed]: 0,
    [UploadFailureReason.EmptyFile]: 0,
  },
  deletionsByReason: {
    staged: 0,
    submitted: 0,
  },
  processed: 0,
  notProcessed: 0,
  stagedFiles: 0,
  submittedFiles: 0,
};

export function formatNumber(num: number): string {
  return num.toLocaleString();
}

export type StageKey =
  | 'attempted'
  | 'uploaded'
  | 'skipped'
  | 'awaiting'
  | 'submitted'
  | 'processed'
  | 'notProcessed'
  | 'deleted';

export interface StageConfig {
  key: StageKey;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  link?: (matterId: string, allDocumentsCaseId: string) => string;
  hasPopover?: boolean;
}

export const STAGE_CONFIGS: Record<StageKey, StageConfig> = {
  attempted: {
    key: 'attempted',
    name: 'Attempted',
    icon: 'lucide--upload',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500',
    borderColor: '#93c5fd', // blue-300
    description: 'Files that were attempted to be uploaded.',
  },
  uploaded: {
    key: 'uploaded',
    name: 'Uploaded',
    icon: 'lucide--check-circle',
    color: 'text-green-500',
    bgColor: 'bg-green-500',
    borderColor: '#86efac', // green-300
    description: 'Files that were successfully uploaded.',
  },
  skipped: {
    key: 'skipped',
    name: 'Skipped',
    icon: 'lucide--ban',
    color: 'text-amber-500',
    bgColor: 'bg-amber-600',
    borderColor: '#fcd34d', // amber-300
    description: 'Files not uploaded due to duplicates, unsupported formats, or other reasons.',
    hasPopover: true,
  },
  awaiting: {
    key: 'awaiting',
    name: 'Awaiting Submission',
    icon: 'lucide--clock',
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500',
    borderColor: '#a5b4fc', // indigo-300
    description: 'Uploaded files that you need to submit for processing.',
    link: (matterId) => `/matters/${matterId}/docs/upload`,
  },
  submitted: {
    key: 'submitted',
    name: 'Submitted',
    icon: 'lucide--send',
    color: 'text-green-500',
    bgColor: 'bg-green-500',
    borderColor: '#86efac', // green-300
    description: 'Files submitted for processing.',
  },
  processed: {
    key: 'processed',
    name: 'Processed',
    icon: 'lucide--check-check',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500',
    borderColor: '#6ee7b7', // emerald-300
    description: 'Files successfully processed by Wexler.',
    link: (matterId, allDocumentsCaseId) => `/matters/${matterId}/subsets/${allDocumentsCaseId}/docs?processed=true`,
  },
  notProcessed: {
    key: 'notProcessed',
    name: 'Not Processed',
    icon: 'lucide--x',
    color: 'text-red-500',
    bgColor: 'bg-red-500',
    borderColor: '#fca5a5', // red-300
    description: 'Files that could not be processed by Wexler.',
    link: (matterId, allDocumentsCaseId) => `/matters/${matterId}/subsets/${allDocumentsCaseId}/docs?processed=false`,
  },
  deleted: {
    key: 'deleted',
    name: 'Deleted',
    icon: 'lucide--trash-2',
    color: 'text-red-500',
    bgColor: 'bg-red-500',
    borderColor: '#fca5a5', // red-300
    description: 'Files deleted from the matter.',
    hasPopover: true,
  },
};

export function getStageCount(stats: FileHistoryStats, key: StageKey): number {
  switch (key) {
    case 'attempted':
      return stats.totalAttempted;
    case 'uploaded':
      return stats.totalSuccessful;
    case 'skipped':
      return stats.totalFailed;
    case 'awaiting':
      return stats.stagedFiles;
    case 'submitted':
      return stats.submittedFiles;
    case 'processed':
      return stats.processed;
    case 'notProcessed':
      return stats.notProcessed;
    case 'deleted':
      return stats.deletionsByReason.staged + stats.deletionsByReason.submitted;
    default:
      return 0;
  }
}
